import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchPrintData } from '../../api/worksheets';
import styles from './WorksheetPrintView.module.css';

function WorksheetPrintView() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const showKey = searchParams.get('key') === 'true';
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPrintData();
  }, [id, showKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrintData = async () => {
    setLoading(true);
    try {
      const result = await fetchPrintData(parseInt(id), showKey);
      setWorksheet(result.data || result.worksheet || result);
    } catch (err) {
      setError('Failed to load worksheet');
    } finally {
      setLoading(false);
    }
  };

  const toggleKey = () => {
    setSearchParams(showKey ? {} : { key: 'true' });
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!worksheet) return null;

  return (
    <div className={styles.printView}>
      {/* Controls (hidden in print) */}
      <div className={styles.controls}>
        <button onClick={() => window.print()} className={styles.printButton}>
          Print
        </button>
        <button onClick={toggleKey} className={styles.toggleButton}>
          {showKey ? 'Student Version' : 'Answer Key'}
        </button>
        <button onClick={() => window.history.back()} className={styles.backButton}>
          Back to Editor
        </button>
      </div>

      {/* Printable Content */}
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>{worksheet.title}</h1>
          {showKey && <div className={styles.keyBanner}>ANSWER KEY</div>}
          <div className={styles.infoRow}>
            <span>Name: ____________________</span>
            <span>Date: ____________________</span>
            <span>Period: ________</span>
          </div>
        </div>

        {/* Instructions */}
        {worksheet.instructions && (
          <div className={styles.instructions}>
            <strong>Instructions:</strong> {worksheet.instructions}
          </div>
        )}

        {/* Questions */}
        <div className={styles.questions}>
          {(worksheet.questions || [])
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((question, index) => (
              <div key={question.id || index} className={styles.question}>
                <div className={styles.questionHeader}>
                  <span className={styles.questionNumber}>{index + 1}.</span>
                  <span className={styles.questionText}>{question.questionText}</span>
                  {question.points > 1 && (
                    <span className={styles.points}>({question.points} pts)</span>
                  )}
                </div>

                {/* MC options */}
                {(question.questionType === 'multiple_choice' || question.questionType === 'true_false') && (
                  <div className={styles.options}>
                    {(question.options || [])
                      .sort((a, b) => a.order - b.order)
                      .map((opt, i) => (
                        <div
                          key={i}
                          className={`${styles.option} ${showKey && opt.isCorrect ? styles.correctOption : ''}`}
                        >
                          <span className={styles.optionLabel}>
                            {String.fromCharCode(65 + i)})
                          </span>
                          <span>{opt.text}</span>
                          {showKey && opt.isCorrect && (
                            <span className={styles.correctMark}> ✓</span>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Short answer */}
                {question.questionType === 'short_answer' && (
                  <div className={styles.answerArea}>
                    {showKey && question.correctAnswer ? (
                      <div className={styles.correctAnswer}>
                        Answer: {question.correctAnswer}
                      </div>
                    ) : (
                      <div className={styles.answerLines}>
                        <div className={styles.answerLine}></div>
                        <div className={styles.answerLine}></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Open-ended */}
                {question.questionType === 'open_ended' && (
                  <div className={styles.workArea}>
                    {showKey && question.workedSolution ? (
                      <div className={styles.workedSolution}>
                        <strong>Solution:</strong>
                        <pre className={styles.solutionText}>{question.workedSolution}</pre>
                      </div>
                    ) : (
                      <div className={styles.workSpace}>
                        {/* Empty space for student work */}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default WorksheetPrintView;
